import { Close } from "@mui/icons-material";
import { Button, IconButton, Slide, Snackbar, Stack } from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import { AppConstant, LangConstant, SystemConstant } from "const";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConversationActions, ConversationSelectors, SystemActions } from "redux-store";
import { getInteractor } from "services/local.service";
import { restoreDeleteOnlyMe } from "services/message.service";
import { convertString2JSON, StorageUtil } from "utils";
import { getNSLang } from "utils/lang.utils";

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const DEFAULT_COUNTDOWN_TIME = 5000; // millisecond

const DeleteOnlyMeSnackbar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  let countdownInterval = null;
  let snackbarTimeout = null;

  const deleteOnlyMeMsg = useSelector(ConversationSelectors.getDeleteOnlyMeMsg);

  const [open, setOpen] = useState(false);
  const [countDown, setCountDown] = useState(5);

  const handleClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
    setCountDown(5);
  };

  const handleRestoreMessage = async () => {
    if (deleteOnlyMeMsg && deleteOnlyMeMsg.message && deleteOnlyMeMsg.message.id) {
      // Update local db
      await restoreDeleteOnlyMe(prefixKey, deleteOnlyMeMsg.message);
      // Trigger UI
      dispatch(
        ConversationActions.conversationSet({
          deleteOnlyMeMsg: { ...deleteOnlyMeMsg, isDelete: false },
        }),
      );

      // Trigger update UI total reply in parent message
      const deleteMessage = deleteOnlyMeMsg.message;
      const sendAsDirectMessage = convertString2JSON(deleteMessage.options, {})?.send_as_direct_message;
      if (
        sendAsDirectMessage === SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect &&
        Boolean(deleteMessage.threadId)
      ) {
        const parentMessageList = await getInteractor(prefixKey).LocalMessageService.find({
          source_id: deleteMessage.threadId,
          send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
          state: SystemConstant.STATE.active,
        });
        dispatch(
          ConversationActions.conversationSet({
            updateMessageStatus: { updateMessageIds: parentMessageList.map(item => item.id), modified: Date.now() },
          }),
        );
      }
    }

    handleClose();
  };

  const handleExecute = async () => {
    await getInteractor(prefixKey).LocalApiCallService.update(
      {
        task: AppConstant.API_CALL_TASK.message,
      },
      {
        task: AppConstant.API_CALL_TASK.delayMessage,
      },
    );

    dispatch(SystemActions.executeQueue());
    dispatch(
      ConversationActions.conversationSet({
        deleteOnlyMeMsg: {
          message: null,
          isDelete: true,
        },
      }),
    );
    handleClose();
  };

  const handleCountdown = () => {
    if (countdownInterval) clearInterval(countdownInterval);

    setCountDown(5);
    let countdownTime = DEFAULT_COUNTDOWN_TIME;
    countdownInterval = setInterval(() => {
      if (countdownTime > 0) {
        const countdown = Math.round(countdownTime / 1000);
        setCountDown(countdown);
        countdownTime = countdownTime - 1000;
      } else {
        clearInterval(countdownInterval);
        handleExecute();
      }
    }, 1000);
  };

  useEffect(() => {
    if (deleteOnlyMeMsg && deleteOnlyMeMsg.message && deleteOnlyMeMsg.isDelete) {
      if (open) {
        setOpen(false);

        setTimeout(() => {
          handleCountdown();
          setOpen(true);
        }, 300);
      } else {
        handleCountdown();
        setOpen(true);
      }
    } else {
      setOpen(false);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (snackbarTimeout) clearTimeout(snackbarTimeout);
    };
  }, [deleteOnlyMeMsg]);

  useEffect(() => {
    if (!open) {
      if (countdownInterval) clearInterval(countdownInterval);
      if (snackbarTimeout) clearTimeout(snackbarTimeout);
    }
  }, [open]);

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      message={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETED_MESSAGE")}
      action={
        <Stack direction="row" alignItems="center">
          <RestoreButton onClick={handleRestoreMessage}>
            {getNSLang(LangConstant.NS_HOME_CONVERSATION, "FM_RESTORE_MSG_COUNTDOWN", { countdown: countDown })}
          </RestoreButton>
          <IconButton onClick={handleExecute}>
            <Close color="primary" />
          </IconButton>
        </Stack>
      }
      TransitionComponent={SlideTransition}
      ContentProps={{ classes: { root: classes.snackbarContent } }}
      className={classes.snackbar}
    />
  );
};

export default DeleteOnlyMeSnackbar;

const RestoreButton = styled(Button)(props => ({
  textTransform: "none",
  color: props.theme.palette.primary.main,
}));

const useStyles = makeStyles({
  snackbarContent: {
    width: "30vw",
    minWidth: 290,
    maxWidth: 330,
    borderRadius: 8,
    fontSize: 14,
    backgroundColor: "#434343",
  },

  snackbar: {
    bottom: 76,
  },
});
